import React, { useEffect, useState } from "react";
import { ChatCircleDots, PaperPlaneTilt, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export default function MyFeedbackPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const locale = lang === "fr" ? "fr-CA" : undefined;
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [sending, setSending] = useState(null);

  const load = () => {
    api.get("/feedback/mine").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  };
  useEffect(load, []);

  const sendReply = async (id) => {
    const message = (drafts[id] || "").trim();
    if (!message) return;
    setSending(id);
    try {
      await api.post(`/feedback/${id}/reply`, { message });
      toast.success("Reply sent");
      setDrafts((d) => ({ ...d, [id]: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(null); }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="my-feedback-page">
        <h1 className="text-2xl font-bold font-display mb-1 flex items-center gap-2">
          <ChatCircleDots size={26} weight="duotone" color="#00768a" /> My feedback
          {user?.pilot_tester && (
            <span className="px-2 py-0.5 rounded-full bg-[#FEF3C7] border border-[#F59E0B] text-[#B45309] text-[10px] font-bold tracking-wide" data-testid="my-feedback-pilot-badge">
              PILOT TESTER
            </span>
          )}
        </h1>
        <p className="text-sm text-[#000000] mb-5">Your messages to the developer and their replies.</p>
        {items === null ? (
          <div className="text-[#000000]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-[#C4C8CE] bg-white p-8 text-center text-sm text-[#000000]" data-testid="my-feedback-empty">
            You haven't sent any feedback yet — use the “Send feedback” button anytime.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map((f) => (
              <div key={f.id} data-testid={`my-feedback-item-${f.id}`}
                className={`rounded-xl border px-4 py-3 bg-white ${f.status === "resolved" ? "border-[#C4C8CE]" : "border-[#A3E635]"}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <span className="text-[11px] text-[#000000]">
                    {new Date(f.at).toLocaleString(locale)}{f.page ? ` · ${f.page}` : ""}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${f.status === "resolved" ? "bg-[#EDEEF0] text-[#000000]" : "bg-[rgba(204,255,0,0.5)] text-[#3F6212]"}`}
                    data-testid={`my-feedback-status-${f.id}`}>
                    {f.status === "resolved" ? "RESOLVED" : "ACTIVE"}
                  </span>
                </div>
                {f.rating ? (
                  <div className="flex gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={14} weight={s <= f.rating ? "fill" : "regular"} color="#F59E0B" />
                    ))}
                  </div>
                ) : null}
                {f.message && <div className="text-sm text-[#000000] whitespace-pre-wrap mb-2">{f.message}</div>}
                {(f.replies || []).map((r) => (
                  <div key={r.id}
                    className={`ml-3 border-l-2 pl-3 py-1 mb-2 ${r.from_user ? "border-[#C4C8CE]" : "border-[#00B8D4]"}`}
                    data-testid={`my-feedback-reply-${r.id}`}>
                    <div className="text-[11px] text-[#000000]">
                      <b className={r.from_user ? "text-[#000000]" : "text-[#00768a]"}>{r.from_user ? "You" : r.admin_name}</b>
                      {" · "}{new Date(r.at).toLocaleString(locale)}
                    </div>
                    <div className="text-sm text-[#000000] whitespace-pre-wrap">{r.message}</div>
                  </div>
                ))}
                <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); sendReply(f.id); }}>
                  <input
                    className="input-base flex-1 text-sm py-1.5"
                    placeholder="Write a reply…"
                    value={drafts[f.id] || ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [f.id]: e.target.value }))}
                    maxLength={2000}
                    data-testid={`my-feedback-reply-input-${f.id}`}
                  />
                  <button type="submit" className="btn-secondary text-xs flex items-center gap-1"
                    disabled={sending === f.id || !(drafts[f.id] || "").trim()}
                    data-testid={`my-feedback-reply-send-${f.id}`}>
                    <PaperPlaneTilt size={13} weight="bold" /> {sending === f.id ? "Sending…" : "Reply"}
                  </button>
                </form>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
